import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { z } from 'zod';
import { AppShell } from './components/shell';
import { Empty } from './components/states';
import type { ApiClient } from './lib/api';
import { useT } from './lib/i18n';
import { AccountPage } from './pages/account';
import { CashPage } from './pages/cash';
import { ContractDetailPage } from './pages/contract-detail';
import { DashboardPage } from './pages/dashboard';
import { ImportDetailPage } from './pages/import-detail';
import { ImportsPage } from './pages/imports';
import { InstallmentDetailPage } from './pages/installment-detail';
import { installmentSearchSchema, InstallmentsPage } from './pages/installments';
import { InvitationPage } from './pages/invitation';
import { SignupPage } from './pages/signup';
import {
  MandateSimulationPage,
  SubscriptionReturnPage,
} from './pages/settings/subscription-billing';
import { LoginPage } from './pages/login';
import { PolicyholderDetailPage } from './pages/policyholder-detail';
import { PortfolioPage, portfolioSearchSchema } from './pages/portfolio';
import { ReportsPage } from './pages/reports';
import { ScenarioEditorPage } from './pages/scenario-editor';
import { ScenariosPage, scenariosSearchSchema } from './pages/scenarios';
import { SettingsPage, settingsSearchSchema } from './pages/settings';
import { TemplateEditorPage } from './pages/template-editor';
import { EscalationPage, WorklistPage, worklistSearchSchema } from './pages/worklist';

// Routes du portail (BA-021). Les écrans protégés passent par la route `app`, qui renvoie
// vers la connexion tant que la session n'est pas établie.

export interface RouterContext {
  api: ApiClient;
  queryClient: QueryClient;
}

function NotFound() {
  const t = useT();
  return <Empty>{t.common.notFound}</Empty>;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  notFoundComponent: NotFound,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/connexion',
  validateSearch: z.object({
    redirect: z.string().optional(),
    /** Retour de la connexion Beta Suite (BA-074). */
    sso: z.enum(['ok', 'echec', 'mfa', 'indisponible']).optional(),
  }),
  beforeLoad: ({ context, search }) => {
    if (!context.api.current()) return;
    const target = search.redirect;
    const safe = target?.startsWith('/') && !target.startsWith('//') ? target : undefined;
    throw redirect(safe ? { to: '/', href: safe } : { to: '/' });
  },
  component: LoginPage,
});

const invitationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invitation',
  validateSearch: z.object({ token: z.string().optional() }),
  component: InvitationPage,
});

/** Inscription d'une agence (BA-088), sans compte. */
const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/inscription',
  component: SignupPage,
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: ({ context, location }) => {
    if (!context.api.current()) {
      throw redirect({ href: `/connexion?redirect=${encodeURIComponent(location.href)}` });
    }
  },
  component: AppShell,
});

const page = <P extends string>(path: P, component: () => React.ReactNode) =>
  createRoute({ getParentRoute: () => appRoute, path, component });

const routeTree = rootRoute.addChildren([
  loginRoute,
  invitationRoute,
  signupRoute,
  appRoute.addChildren([
    page('/', DashboardPage),
    // Retour du prestataire après la signature du mandat de prélèvement (BA-089).
    page('/abonnement', SubscriptionReturnPage),
    createRoute({
      getParentRoute: () => appRoute,
      path: '/abonnement/simulation-mandat',
      validateSearch: z.object({ mandat: z.string().optional() }),
      component: MandateSimulationPage,
    }),
    createRoute({
      getParentRoute: () => appRoute,
      path: '/echeances',
      validateSearch: installmentSearchSchema,
      component: InstallmentsPage,
    }),
    page('/echeances/$id', () => <InstallmentDetailPage />),
    createRoute({
      getParentRoute: () => appRoute,
      path: '/portefeuille',
      validateSearch: portfolioSearchSchema,
      component: PortfolioPage,
    }),
    page('/contrats/$id', ContractDetailPage),
    page('/assures/$id', PolicyholderDetailPage),
    createRoute({
      getParentRoute: () => appRoute,
      path: '/scenarios',
      validateSearch: scenariosSearchSchema,
      component: ScenariosPage,
    }),
    page('/scenarios/$id', ScenarioEditorPage),
    page('/modeles/$id', TemplateEditorPage),
    createRoute({
      getParentRoute: () => appRoute,
      path: '/file-de-travail',
      validateSearch: worklistSearchSchema,
      component: WorklistPage,
    }),
    page('/file-de-travail/$id', EscalationPage),
    page('/imports', ImportsPage),
    page('/imports/$id', ImportDetailPage),
    page('/rapports', ReportsPage),
    page('/caisse', CashPage),
    page('/compte', AccountPage),
    createRoute({
      getParentRoute: () => appRoute,
      path: '/parametres',
      validateSearch: settingsSearchSchema,
      component: SettingsPage,
    }),
  ]),
]);

export function createAppRouter(context: RouterContext) {
  return createRouter({ routeTree, context, defaultPreload: 'intent', scrollRestoration: true });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
